import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-hot-toast";

interface UseInviteEmployeeModalProps {
  onInvite: (emails: string[]) => void;
  onClose: () => void;
}

export const useInviteEmployeeModal = ({ onInvite, onClose }: UseInviteEmployeeModalProps) => {
  const { t } = useTranslation();
  const [emails, setEmails] = useState("");
  const [emailList, setEmailList] = useState<string[]>([]);
  const [csvEmails, setCsvEmails] = useState<string[]>([]);
  const [csvFileName, setCsvFileName] = useState("");

  // Handle CSV file upload and parsing
  const handleCsvUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (file.type !== "text/csv" && !file.name.endsWith(".csv")) {
      toast.error(t("admin.invalidCsvFile", "Please select a valid CSV file"));
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error(t("admin.csvFileTooLarge", "File size must be less than 5MB"));
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      const lines = text.split("\n");
      const emails: string[] = [];

      lines.forEach((line, index) => {
        // Skip header row (first line)
        if (index === 0) return;

        const email = line.trim();
        if (email && email.includes("@")) {
          emails.push(email);
        }
      });

      if (emails.length > 0) {
        setCsvEmails(emails);
        setCsvFileName(file.name);
        toast.success(t("admin.csvProcessed", `${emails.length} emails found in CSV`));
      } else {
        toast.error(t("admin.noValidEmails", "No valid emails found in CSV file"));
      }
    };

    reader.onerror = () => {
      toast.error(t("admin.csvReadError", "Error reading CSV file"));
    };

    reader.readAsText(file);
  };

  const handleEmailChange = (value: string) => {
    setEmails(value);
    // Split by comma, newline, or semicolon and filter out empty strings
    const emailArray = value
      .split(/[,\n;]/)
      .map(email => email.trim())
      .filter(email => email.length > 0);
    setEmailList(emailArray);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Combine CSV emails and manually entered emails
    const allEmails = [...csvEmails, ...emailList];
    const uniqueEmails = [...new Set(allEmails)];
    
    if (uniqueEmails.length > 0) {
      onInvite(uniqueEmails);
      clearCsvData();
    }
  };

  const handleClose = () => {
    clearCsvData();
    onClose();
  };

  const clearCsvData = () => {
    setCsvEmails([]);
    setCsvFileName("");
    setEmails("");
    setEmailList([]);
  };

  const totalEmails = [...csvEmails, ...emailList].length;

  return {
    emails,
    emailList,
    csvEmails,
    csvFileName,
    totalEmails,
    handleCsvUpload,
    handleEmailChange,
    handleSubmit,
    handleClose,
    clearCsvData,
  };
};
